import { type DelimiterType, type MarkdownConfig } from "@lezer/markdown";

/** Lezer markdown extensions teaching the parser the studio's non-CommonMark
 *  inline syntax, so the *syntax tree* carries `==highlight==`, `[[wikilinks]]`,
 *  and `$math$` as real nodes. The live-preview decoration layer then styles and
 *  hides them off the tree (parity with the react-markdown `remarkHighlight` /
 *  `remarkWikiLink` plugins). Tree-driven, like the renderer's `findAndReplace`,
 *  means these never fire inside code spans — `` `==x==` `` stays literal. */

const EQ = 61; // '='
const LBRACKET = 91; // '['
const RBRACKET = 93; // ']'
const DOLLAR = 36; // '$'
const ASTERISK = 42; // '*'
const UNDERSCORE = 95; // '_'
const TILDE = 126; // '~'

const isEdgeSpace = (s: string) => /\s|^$/.test(s);

/** `==highlight==` → `Highlight` (marks: `HighlightMark`).
 *
 *  Unlike CommonMark emphasis, a `==` delimiter may hug whitespace: `== spaced ==`
 *  highlights. Emphasis cannot afford that rule (it would make `2 * 3 * 4` italic
 *  and `snake_case` a mess) but `==` is not a character prose uses casually, and
 *  refusing the spaced form meant the highlight vanished the moment someone typed
 *  a space after the marker — the effect flickering off mid-word is exactly what
 *  a live preview should not do. `remarkHighlight` (the read-only Preview) is
 *  relaxed to match; the two must agree.
 *
 *  A bare `====` still parses as an empty Highlight, and the live-preview layer
 *  deliberately leaves an EMPTY span's markers visible so a half-typed marker
 *  never disappears out from under the caret. */
const HighlightDelim: DelimiterType = { resolve: "Highlight", mark: "HighlightMark" };
const Highlight: MarkdownConfig = {
  defineNodes: [{ name: "Highlight" }, { name: "HighlightMark" }],
  parseInline: [
    {
      name: "Highlight",
      parse(cx, next, pos) {
        if (next !== EQ || cx.char(pos + 1) !== EQ) return -1;
        return cx.addDelimiter(HighlightDelim, pos, pos + 2, true, true);
      },
      after: "Emphasis",
    },
  ],
};

/** `[[Target]]` / `[[Target|alias]]` → a single `WikiLink` node bracketed by two
 *  `WikiLinkMark`s. Parsed as one element (not a symmetric delimiter) by scanning
 *  forward to the closing `]]` within the current inline run. */
const WikiLink: MarkdownConfig = {
  defineNodes: [{ name: "WikiLink" }, { name: "WikiLinkMark" }],
  parseInline: [
    {
      name: "WikiLink",
      parse(cx, next, pos) {
        if (next !== LBRACKET || cx.char(pos + 1) !== LBRACKET) return -1;
        let scan = pos + 2;
        for (;;) {
          const c = cx.char(scan);
          if (c === -1) return -1; // no closer in this inline run
          if (c === RBRACKET && cx.char(scan + 1) === RBRACKET) break;
          scan++;
        }
        const closeTo = scan + 2;
        return cx.addElement(
          cx.elt("WikiLink", pos, closeTo, [
            cx.elt("WikiLinkMark", pos, pos + 2),
            cx.elt("WikiLinkMark", scan, closeTo),
          ]),
        );
      },
      before: "Link",
    },
  ],
};

/** `$inline$` math → `InlineMath` (marks: `MathMark`). `$$block$$` is left raw on
 *  purpose (it renders in Preview mode); open/close gating follows remark-math:
 *  no space-hug, and an opening `$` isn't a currency `$5`. */
const MathDelim: DelimiterType = { resolve: "InlineMath", mark: "MathMark" };
const InlineMath: MarkdownConfig = {
  defineNodes: [{ name: "InlineMath" }, { name: "MathMark" }],
  parseInline: [
    {
      name: "InlineMath",
      parse(cx, next, pos) {
        if (next !== DOLLAR || cx.char(pos + 1) === DOLLAR) return -1;
        const after = cx.slice(pos + 1, pos + 2);
        const spaceBefore = isEdgeSpace(cx.slice(pos - 1, pos));
        const spaceAfter = isEdgeSpace(after);
        const digitAfter = /\d/.test(after);
        return cx.addDelimiter(MathDelim, pos, pos + 1, !spaceAfter && !digitAfter, !spaceBefore);
      },
      after: "Emphasis",
    },
  ],
};

/** Whitespace-tolerant `**bold**`, `__bold__` and `~~struck~~`.
 *
 *  CommonMark refuses a delimiter that hugs whitespace, so `** bold **` and even
 *  `**bold **` stay literal — the effect blinks out the moment you type a space
 *  next to a marker, which on a live-preview surface reads as the editor
 *  breaking. The rule exists to stop `2 * 3 * 4` becoming italic, and that
 *  hazard is entirely about SINGLE-character runs: `**`, `__` and `~~` do not
 *  occur in ordinary prose, so they can be relaxed in BOTH directions safely
 *  while single `*` and `_` keep CommonMark's rule exactly.
 *
 *  Only a run of EXACTLY two is claimed. `***both***` and `____` are left to the
 *  built-in parser, which already resolves the nesting properly. */
function relaxedPair(
  char: number,
  name: string,
  resolve: string,
  mark: string,
  before: string,
): MarkdownConfig {
  const delim: DelimiterType = { resolve, mark };
  return {
    parseInline: [
      {
        name,
        parse(cx, next, pos) {
          if (next !== char || cx.char(pos + 1) !== char) return -1;
          // A longer run belongs to the built-in parser.
          if (cx.char(pos + 2) === char || cx.char(pos - 1) === char) return -1;
          return cx.addDelimiter(delim, pos, pos + 2, true, true);
        },
        before,
      },
    ],
  };
}

const RelaxedStrongStar = relaxedPair(ASTERISK, "RelaxedStrongStar", "StrongEmphasis", "EmphasisMark", "Emphasis");
const RelaxedStrongUnderscore = relaxedPair(UNDERSCORE, "RelaxedStrongUnderscore", "StrongEmphasis", "EmphasisMark", "Emphasis");
const RelaxedStrike = relaxedPair(TILDE, "RelaxedStrike", "Strikethrough", "StrikethroughMark", "Strikethrough");

/** Pass to `markdown({ extensions: graphmindMarkdown })`. */
export const graphmindMarkdown: MarkdownConfig[] = [
  Highlight,
  WikiLink,
  InlineMath,
  RelaxedStrongStar,
  RelaxedStrongUnderscore,
  RelaxedStrike,
];
